import React from "react";
import CanvasRenderer from "./CanvasRenderer";

const MINIMAP_HEIGHT = 300;
const MINIMAP_MAX_HEIGHT = 300;

const MiniMap = ({ cards, gridOffset, zoom, centerPanelSize, onJumpTo, showImages, backgroundImage = 'default' }) => {
  // Get the actual container width for proper scaling
  const [containerWidth, setContainerWidth] = React.useState(300);
  const [screenAspect, setScreenAspect] = React.useState(1.0);
  const containerRef = React.useRef(null);

  // Update container width and screen aspect when component mounts or resizes
  React.useEffect(() => {
    const updateWidthAndAspect = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
      }
      setScreenAspect(window.innerWidth / window.innerHeight);
    };
    updateWidthAndAspect();
    // Use ResizeObserver for container width
    let observer;
    if (containerRef.current) {
      observer = new ResizeObserver(updateWidthAndAspect);
      observer.observe(containerRef.current);
    }
    // Listen for window resize for aspect ratio
    window.addEventListener('resize', updateWidthAndAspect);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateWidthAndAspect);
    };
  }, []);

  // Compute all minimap logic in one place so rendering and click mapping are always in sync
  const minimapLogic = React.useMemo(() => {
    // Compute bounds of all cards (with some padding)
    let minX = 0, minY = 0, maxX = 1, maxY = 1;
    if (cards.length) {
      minX = Infinity; minY = Infinity; maxX = -Infinity; maxY = -Infinity;
      for (const card of cards) {
        minX = Math.min(minX, card.pos_x);
        minY = Math.min(minY, card.pos_y);
        maxX = Math.max(maxX, card.pos_x + 1);
        maxY = Math.max(maxY, card.pos_y + 1);
      }
      // Add some padding around the cards (2 units on each side)
      const padding = 2;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;
      // If viewport exists, expand bounds to include it
      if (centerPanelSize && zoom) {
        const CELL_SIZE = 122;
        const viewW = centerPanelSize.width / (CELL_SIZE * zoom);
        const viewH = centerPanelSize.height / (CELL_SIZE * zoom);
        const centerCellX = -gridOffset.x / (CELL_SIZE * zoom);
        const centerCellY = -gridOffset.y / (CELL_SIZE * zoom);
        const viewportX = centerCellX - viewW / 2;
        const viewportY = centerCellY - viewH / 2;
        const viewportMinX = viewportX;
        const viewportMinY = viewportY;
        const viewportMaxX = viewportX + viewW;
        const viewportMaxY = viewportY + viewH;
        minX = Math.min(minX, viewportMinX);
        minY = Math.min(minY, viewportMinY);
        maxX = Math.max(maxX, viewportMaxX);
        maxY = Math.max(maxY, viewportMaxY);
      }
    }
    const logicalWidth = Math.max(1, maxX - minX);
    const logicalHeight = Math.max(1, maxY - minY);
    const minSize = 3;
    const adjustedWidth = Math.max(logicalWidth, minSize);
    const adjustedHeight = Math.max(logicalHeight, minSize);
    return { bounds: { minX, minY, maxX, maxY }, adjustedWidth, adjustedHeight };
  }, [cards, centerPanelSize, zoom, gridOffset]);

  // Map cards to CanvasRenderer format (relative to bounds)
  const mappedCards = React.useMemo(() => {
    if (!cards.length) return [];
    return cards.map(card => ({
      card_id: card.card_id,
      x: card.pos_x - minimapLogic.bounds.minX,
      y: card.pos_y - minimapLogic.bounds.minY,
      width: 1,
      height: 1,
      background: showImages ? (card.custom_background || card.default_background) : card.default_background,
      texts: card.texts || null,
      selected_option_index: card.selected_option_index,
    }));
  }, [cards, minimapLogic]);

  // Calculate scale to fit cards within minimap area
  const containerHeight = Math.min(containerWidth / screenAspect, 800);
  const scaleX = containerWidth / minimapLogic.adjustedWidth;
  const scaleY = containerHeight / minimapLogic.adjustedHeight;
  const scale = Math.min(scaleX, scaleY);

  // Compute viewport rectangle in minimap coordinates
  let viewport = null;
  if (centerPanelSize && zoom) {
    const CELL_SIZE = 122;
    const viewW = centerPanelSize.width / (CELL_SIZE * zoom);
    const viewH = centerPanelSize.height / (CELL_SIZE * zoom);
    const centerCellX = -gridOffset.x / (CELL_SIZE * zoom);
    const centerCellY = -gridOffset.y / (CELL_SIZE * zoom);
    const viewportX = centerCellX - viewW / 2 - minimapLogic.bounds.minX;
    const viewportY = centerCellY - viewH / 2 - minimapLogic.bounds.minY;
    viewport = {
      x: viewportX,
      y: viewportY,
      width: viewW,
      height: viewH
    };
  }

  // Handle click to jump
  const handleClick = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Center of the minimap container
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Offset from center in pixels
    const offsetX = clickX - centerX;
    const offsetY = clickY - centerY;

    // Convert to logical units
    const logicalOffsetX = offsetX / scale;
    const logicalOffsetY = offsetY / scale;

    // Center of the logical area
    const logicalCenterX = minimapLogic.bounds.minX + minimapLogic.adjustedWidth / 2;
    const logicalCenterY = minimapLogic.bounds.minY + minimapLogic.adjustedHeight / 2;

    // Target logical coordinates
    let targetX = logicalCenterX + logicalOffsetX;
    let targetY = logicalCenterY + logicalOffsetY;

    // Subtract half the viewport size so the center aligns with the click
    if (centerPanelSize && zoom) {
      const CELL_SIZE = 122;
      const viewW = centerPanelSize.width / (CELL_SIZE * zoom);
      const viewH = centerPanelSize.height / (CELL_SIZE * zoom);
      targetX += viewW / 2;
      targetY += viewH / 2;
    }

    if (onJumpTo) onJumpTo(targetX, targetY);
  };

  return (
    <div
      id="tour-minimap"
      style={{
        width: "100%",
        height: containerHeight,
        maxHeight: containerHeight,
        minHeight: 80,
        background: backgroundImage && backgroundImage !== 'default'
          ? `url("/images/${backgroundImage}") repeat`
          : 'radial-gradient(circle at center, #1e293b 0%, #0f172a 100%)',
        borderRadius: 8,
        border: "1px solid rgba(255, 255, 255, 0.1)",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer"
      }}
      onClick={handleClick}
      ref={containerRef}
    >
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CanvasRenderer
          cards={mappedCards}
          width={minimapLogic.adjustedWidth}
          height={minimapLogic.adjustedHeight}
          mode="minimap"
          viewport={viewport}
          showMiniMap={false}
          scale={scale}
        />
      </div>
    </div>
  );
};

export default MiniMap; 